let ctx: AudioContext | null = null;
let master: GainNode | null = null;
// Split master into two siblings — music routes through musicBus, SFX
// through sfxBus. Lets the player tune them independently from the
// settings modal. Both feed master, master feeds destination.
let musicBus: GainNode | null = null;
let sfxBus: GainNode | null = null;

const VOL_KEY_MUSIC = 'smp.musicVol';
const VOL_KEY_SFX = 'smp.sfxVol';

// User-chosen music volume (0..1). Persisted via setMusicVolume; ducked
// transiently via setMusicDuck without overwriting this. Effective bus
// gain = musicUserVol * musicDuckFactor * introMuteFactor.
let musicUserVol = 1;
let musicDuckFactor = 1;
// Hard mute applied while a boot-time intro / cutscene wants the music
// silenced regardless of the per-frame ducking the app loop applies.
// `setMusicDuck` is called every frame from app.ts and would otherwise
// override anything we set there. This factor multiplies through and
// is the boss — flip it via `setIntroMusicMute(true|false)`.
let introMuteFactor = 1;

function loadStoredVol(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const n = parseFloat(raw);
    if (!isFinite(n)) return fallback;
    return Math.max(0, Math.min(1, n));
  } catch { return fallback; }
}

// ---- Positional audio listener ----
// Sounds wrapped in playAt(x, z, fn) scale their oscillator gain by a
// distance-based falloff curve. Listener position is updated from the
// client's controller pos each frame via setAudioListener.
let listenerX = 0;
let listenerZ = 0;
// Transient multiplier read by blip/sweep/noiseBurst. Reset to 1 after
// every playAt() call so global/non-positional SFX keep working.
let positionalGainMult = 1;

const AUDIO_NEAR = 8;   // full volume within this radius
const AUDIO_FAR  = 50;  // linear falloff 1 → 0.1 between NEAR and FAR
const AUDIO_CUT  = 80;  // 0.1 → 0 between FAR and CUT; silent beyond

export function setAudioListener(x: number, z: number): void {
  listenerX = x;
  listenerZ = z;
}

function distanceGain(x: number, z: number): number {
  const dx = x - listenerX;
  const dz = z - listenerZ;
  const dist = Math.hypot(dx, dz);
  if (dist <= AUDIO_NEAR) return 1;
  if (dist >= AUDIO_CUT) return 0;
  if (dist <= AUDIO_FAR) {
    const t = (dist - AUDIO_NEAR) / (AUDIO_FAR - AUDIO_NEAR);
    return 1 - t * 0.9; // 1.0 → 0.1 in the info band
  }
  const t = (dist - AUDIO_FAR) / (AUDIO_CUT - AUDIO_FAR);
  return 0.1 * (1 - t); // 0.1 → 0 in the fade tail
}

// Run `fn` with the positional gain multiplier set so any blip/sweep/
// noiseBurst it triggers is attenuated for a world-space source at
// (x, z). Skips entirely if the source is beyond CUTOFF — saves work on
// a no-op.
export function playAt(x: number, z: number, fn: () => void): void {
  const g = distanceGain(x, z);
  if (g <= 0.01) return;
  positionalGainMult = g;
  try {
    fn();
  } finally {
    positionalGainMult = 1;
  }
}

function getCtx(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext;
    if (AC) {
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = 0.7;
      master.connect(ctx.destination);
      musicBus = ctx.createGain();
      musicUserVol = loadStoredVol(VOL_KEY_MUSIC, 1.0);
      musicBus.gain.value = musicUserVol * musicDuckFactor * introMuteFactor;
      musicBus.connect(master);
      sfxBus = ctx.createGain();
      sfxBus.gain.value = loadStoredVol(VOL_KEY_SFX, 1.0);
      sfxBus.connect(master);
    }
  } catch (e) {
    console.warn('[audio] getCtx failed', e);
    ctx = null;
  }
  return ctx;
}

// Force-resume AudioContext. Safe to call from any user gesture handler;
// no-op if already running. Settings modal calls this on open so volume
// + preview interactions take effect immediately.
export function resumeAudio(): Promise<void> {
  const c = getCtx();
  if (!c) return Promise.resolve();
  if (c.state === 'running') {
    flushPendingSfx();
    prebakeAudio();
    return Promise.resolve();
  }
  return c.resume().then(() => {
    flushPendingSfx();
    prebakeAudio();
  }).catch((e) => {
    console.warn('[audio] resume failed', e);
  });
}

// First playback through Web Audio's oscillator + gain graph has a
// browser-side compile latency that audibly stutters the FIRST blip a
// player hears. Pre-warm the graph by spending 1ms on a silent
// oscillator + buffer source the moment the context unlocks. Idempotent.
let prebaked = false;
function prebakeAudio(): void {
  const c = ctx;
  if (!c || prebaked) return;
  prebaked = true;
  try {
    // Silent buffer through a gain node to warm the buffer-source path.
    const buf = c.createBuffer(1, 1, c.sampleRate);
    const bufSrc = c.createBufferSource();
    const bufGain = c.createGain();
    bufGain.gain.value = 0;
    bufSrc.buffer = buf;
    bufSrc.connect(bufGain);
    bufGain.connect(c.destination);
    bufSrc.start(0);
    // Brief silent oscillator to warm the osc + gain envelope path used
    // by every blip/sweep call.
    const osc = c.createOscillator();
    const oscGain = c.createGain();
    oscGain.gain.value = 0;
    osc.connect(oscGain);
    oscGain.connect(c.destination);
    osc.start(c.currentTime);
    osc.stop(c.currentTime + 0.005);
  } catch (e) {
    console.warn('[audio] prebake failed', e);
  }
}

// Browsers block AudioContext until a user gesture. Install a global
// once-only listener at boot so the FIRST keydown / pointerdown / touch
// flips the ctx to running — no need for the player to find the settings
// modal or the audio-enable hint. Idempotent.
let unlockInstalled = false;
export function installAudioUnlock(): void {
  if (unlockInstalled) return;
  unlockInstalled = true;
  const tryUnlock = () => { void resumeAudio(); };
  addEventListener('keydown',     tryUnlock, { once: false, passive: true });
  addEventListener('pointerdown', tryUnlock, { once: false, passive: true });
  addEventListener('touchstart',  tryUnlock, { once: false, passive: true });
}

// Pending-SFX queue — sounds that fired before the ctx was running get
// queued (cap 16, FIFO) and replayed on the first successful resume.
// Without this, the boot SFX silently vanish on the very first session.
const MAX_PENDING = 16;
const pendingSfx: Array<() => void> = [];
function queueSfx(fn: () => void): void {
  const c = ctx;
  if (c && c.state === 'running') {
    fn();
    return;
  }
  if (pendingSfx.length >= MAX_PENDING) pendingSfx.shift();
  pendingSfx.push(fn);
}
function flushPendingSfx(): void {
  if (pendingSfx.length === 0) return;
  const drain = pendingSfx.splice(0, pendingSfx.length);
  for (const fn of drain) {
    try { fn(); } catch (e) { console.warn('[audio] queued SFX threw', e); }
  }
}

// Diagnostic snapshot of audio bus state. Available via window.smpAudio
// for in-browser debugging of the audio pipeline.
export function audioDiag(): Record<string, unknown> {
  return {
    ctxState: ctx?.state ?? 'no-ctx',
    masterGain: master?.gain.value ?? null,
    musicBusGain: musicBus?.gain.value ?? null,
    sfxBusGain: sfxBus?.gain.value ?? null,
    storedMusicVol: loadStoredVol(VOL_KEY_MUSIC, 1.0),
    storedSfxVol: loadStoredVol(VOL_KEY_SFX, 1.0),
    currentSong: music.getCurrentSongId(),
    nextSong: music.getNextSongId(),
    tempoScale: music.getTempoScale(),
    musicStarted: music.isStarted(),
    currentBar: music.getCurrentBar(),
    currentStep: music.getCurrentStep(),
    currentChordIdx: music.getCurrentChordIdx(),
    progressionLength: music.getProgressionLength(),
    effectiveBpm: music.getEffectiveBpm(),
    barDurationSec: music.getBarDurationSec(),
    previewRemainingMs: music.getPreviewRemainingMs(),
  };
}

// Loud test beep — fires a sine into the SFX bus so the user can
// verify the audio path works at all (audio context resumed, sfxBus
// connected, master alive). Wired to a button in settings.
export function testBeep(): void {
  resumeAudio();
  const c = getCtx();
  const out = dest();
  if (!c || !out) {
    console.warn('[audio] testBeep — no ctx/out');
    return;
  }
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = 'sine';
  osc.frequency.value = 880;
  g.gain.setValueAtTime(0.25, c.currentTime);
  g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.4);
  osc.connect(g).connect(out);
  osc.start();
  osc.stop(c.currentTime + 0.42);
}

// SFX destination — every blip/sweep/noiseBurst feeds this.
function dest(): AudioNode | null {
  const c = getCtx();
  return c && sfxBus ? sfxBus : null;
}

// Music destination — MusicEngine pad/rhythm/melodic/cluster buses feed this.
function musicDest(): AudioNode | null {
  const c = getCtx();
  return c && musicBus ? musicBus : null;
}

export function setMusicVolume(v: number): void {
  const clamped = Math.max(0, Math.min(1, v));
  musicUserVol = clamped;
  try { localStorage.setItem(VOL_KEY_MUSIC, String(clamped)); } catch { /* */ }
  applyMusicGain();
}

// Transient duck factor for things like low-HP music ducking. Doesn't
// persist or overwrite the user's volume preference. Called per frame is
// fine — we only touch the WebAudio gain node, no localStorage.
export function setMusicDuck(factor: number): void {
  musicDuckFactor = Math.max(0, Math.min(1, factor));
  applyMusicGain();
}

// Smooth fade-to-silence on the music bus over `durationMs`. Used by
// the sleep transition (bed → Vibe Jam) so the soundtrack drifts out
// instead of cutting. The user's stored musicUserVol is untouched,
// so reloading the page restores the configured level.
export function fadeMusicOut(durationMs: number): void {
  const c = getCtx();
  if (!c || !musicBus) return;
  const now = c.currentTime;
  const endAt = now + Math.max(0.05, durationMs / 1000);
  musicBus.gain.cancelScheduledValues(now);
  musicBus.gain.setValueAtTime(musicBus.gain.value, now);
  musicBus.gain.linearRampToValueAtTime(0, endAt);
}

function applyMusicGain(): void {
  const c = getCtx();
  if (!c || !musicBus) return;
  const target = musicUserVol * musicDuckFactor * introMuteFactor;
  musicBus.gain.cancelScheduledValues(c.currentTime);
  musicBus.gain.setValueAtTime(target, c.currentTime);
}

// Boot-time / cutscene hard mute. Multiplies into the music bus
// alongside the user-volume and per-frame duck. Use this when the
// procedural soundtrack must NOT bleed under another audio source
// (entrance video, scripted cinematic). `setMusicDuck` calls from
// the per-frame loop won't override this.
//
// Optional `fadeMs` ramps `introMuteFactor` smoothly via rAF so the
// per-frame `applyMusicGain` calls from app.ts pick up the new
// value each tick. Mute is always instant (no fade-out); only the
// unmute path respects the fade.
let introFadeRaf: number | null = null;
export function setIntroMusicMute(muted: boolean, fadeMs = 0): void {
  if (introFadeRaf !== null) {
    cancelAnimationFrame(introFadeRaf);
    introFadeRaf = null;
  }
  if (muted || fadeMs <= 0) {
    introMuteFactor = muted ? 0 : 1;
    applyMusicGain();
    return;
  }
  const startVal = introMuteFactor;
  const startAt = performance.now();
  const tick = (): void => {
    const t = Math.min(1, (performance.now() - startAt) / fadeMs);
    introMuteFactor = startVal + (1 - startVal) * t;
    applyMusicGain();
    if (t < 1) {
      introFadeRaf = requestAnimationFrame(tick);
    } else {
      introFadeRaf = null;
    }
  };
  introFadeRaf = requestAnimationFrame(tick);
}

export function setSfxVolume(v: number): void {
  const clamped = Math.max(0, Math.min(1, v));
  try { localStorage.setItem(VOL_KEY_SFX, String(clamped)); } catch { /* */ }
  const c = getCtx();
  if (!c || !sfxBus) {
    console.warn('[audio] setSfxVolume — no ctx/bus, value persisted only');
    return;
  }
  sfxBus.gain.cancelScheduledValues(c.currentTime);
  sfxBus.gain.setValueAtTime(clamped, c.currentTime);
}

export function getMusicVolume(): number { return loadStoredVol(VOL_KEY_MUSIC, 1.0); }
export function getSfxVolume(): number { return loadStoredVol(VOL_KEY_SFX, 1.0); }

function blip(freq: number, dur: number, type: OscillatorType = 'sine', vol = 0.08) {
  const c = getCtx();
  const out = dest();
  if (!c || !out) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.value = vol * positionalGainMult;
  osc.connect(gain).connect(out);
  osc.start();
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  osc.stop(c.currentTime + dur);
}

function sweep(from: number, to: number, dur: number, type: OscillatorType = 'sine', vol = 0.08) {
  const c = getCtx();
  const out = dest();
  if (!c || !out) return;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(from, c.currentTime);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), c.currentTime + dur);
  gain.gain.value = vol * positionalGainMult;
  gain.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  osc.connect(gain).connect(out);
  osc.start();
  osc.stop(c.currentTime + dur);
}

function noiseBurst(dur: number, vol = 0.06, hp = 600) {
  const c = getCtx();
  const out = dest();
  if (!c || !out) return;
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filt = c.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = hp;
  const gain = c.createGain();
  gain.gain.value = vol * positionalGainMult;
  src.connect(filt).connect(gain).connect(out);
  src.start();
}

// Scheduled variants used by spatialized SFX. Capture positionalGainMult
// at the moment of call (not at fire time) so composite sounds triggered
// inside playAt() keep the correct distance attenuation even when the
// sub-notes are delayed.
function scheduledBlip(delaySec: number, freq: number, dur: number, type: OscillatorType, vol: number) {
  const c = getCtx();
  const out = dest();
  if (!c || !out) return;
  const start = c.currentTime + delaySec;
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(vol * positionalGainMult, start);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain).connect(out);
  osc.start(start);
  osc.stop(start + dur);
}

function scheduledNoiseBurst(delaySec: number, dur: number, vol: number, hp: number) {
  const c = getCtx();
  const out = dest();
  if (!c || !out) return;
  const start = c.currentTime + delaySec;
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filt = c.createBiquadFilter();
  filt.type = 'highpass';
  filt.frequency.value = hp;
  const gain = c.createGain();
  gain.gain.setValueAtTime(vol * positionalGainMult, start);
  src.connect(filt).connect(gain).connect(out);
  src.start(start);
}

// Continuous tether drone — owned for the duration of the tether.
class Drone {
  private osc: OscillatorNode | null = null;
  private gain: GainNode | null = null;
  start() {
    const c = getCtx();
    const out = dest();
    if (!c || !out) return;
    if (this.osc) return;
    this.osc = c.createOscillator();
    this.gain = c.createGain();
    this.osc.type = 'sawtooth';
    this.osc.frequency.value = 130;
    this.gain.gain.value = 0;
    this.osc.connect(this.gain).connect(out);
    this.osc.start();
    this.gain.gain.linearRampToValueAtTime(0.05, c.currentTime + 0.15);
  }
  stop() {
    const c = getCtx();
    if (!c || !this.osc || !this.gain) return;
    this.gain.gain.cancelScheduledValues(c.currentTime);
    this.gain.gain.linearRampToValueAtTime(0, c.currentTime + 0.12);
    this.osc.stop(c.currentTime + 0.13);
    this.osc = null;
    this.gain = null;
  }
}

const tetherDrone = new Drone();

// MusicEngine — procedural multi-song scheduler.
//
// A Song (defined in ./songs.ts) is pure data: scale, chord progression,
// bass/lead/drum step patterns. The engine reads the active song on a
// 16-step-per-bar grid and schedules:
//   - Pad chord voices (re-pitched on bar boundaries via the progression)
//   - Bass + lead one-shots at non-null step entries
//   - Kick / snare / hat at pattern entries (with intensity gates)
//   - Optional sudden-death cluster pad + noise sweep overlay
//
// `playSong(id)` queues a crossfade that completes on the next bar
// boundary so transitions are musical rather than abrupt. `setIntensity`
// scales pad gain + filter cutoff + lead/snare/hat density. `tick(dt)`
// is called per-frame and uses a 0.25s lookahead against the
// AudioContext clock for jitter-free scheduling.
import { SONGS, chordToHz, degreeToHz } from './songs';
import type { Song, SongId } from './songs';

class MusicEngine {
  private current: Song = SONGS.classic;
  private next: Song | null = null;
  private nextFadeMs = 1200;
  private intensity = 0;
  private suddenDeath = false;
  private started = false;

  // Three independent gain buses — pad, melodic (bass+lead), rhythm.
  private padBus: GainNode | null = null;
  private padFilter: BiquadFilterNode | null = null;
  private rhythmBus: GainNode | null = null;
  private melodicBus: GainNode | null = null;

  // Sudden-death dissonant cluster overlay (own bus).
  private clusterBus: GainNode | null = null;
  private clusterVoices: { osc: OscillatorNode; gain: GainNode }[] = [];
  private lastClusterChordIdx = -1;

  // Pad voices currently sounding (one per chord tone).
  private padVoices: { osc: OscillatorNode; gain: GainNode }[] = [];

  // Preview lock — settings modal's preview button sets this so the
  // per-frame auto-selector in main.ts can't immediately override the
  // song the user just picked.
  private previewLock = false;
  private previewUntil = 0;

  // Global tempo multiplier — lets the player audition different BPMs
  // for the same song from settings. 1.0 = song's native BPM. Persists
  // across sessions in localStorage 'smp.tempoScale'.
  private tempoScale = (() => {
    try {
      const raw = localStorage.getItem('smp.tempoScale');
      if (raw === null) return 1.0;
      const n = parseFloat(raw);
      if (!isFinite(n) || n <= 0) return 1.0;
      return Math.max(0.25, Math.min(4.0, n));
    } catch { return 1.0; }
  })();

  // Step clock — 16th notes per bar, monotonic bar count.
  private currentStep = 0;
  private currentBar = 0;
  private nextStepAt = 0;
  // Last chord index we voiced; -1 forces re-pitch on next bar boundary.
  private currentChordIdx = -1;
  private firstAudibleTickLogged = false;
  // AudioContext time when the current song started (set on start + on
  // every swapSong). Used by the timeline HUD for "elapsed seconds".
  private songStartCtxTime = 0;

  start() {
    if (this.started) return;
    const c = getCtx();
    const out = musicDest();
    if (!c || !out) {
      console.warn('[music] start aborted — no ctx/out');
      return;
    }
    this.started = true;
    console.info('[music] start — song=', this.current.id, 'ctxState=', c.state);

    this.padBus = c.createGain();
    this.padFilter = c.createBiquadFilter();
    this.padFilter.type = 'lowpass';
    this.padFilter.frequency.value = this.current.pad.filterHz;
    this.padBus.gain.value = 0;
    this.padFilter.connect(this.padBus).connect(out);
    this.padBus.gain.linearRampToValueAtTime(this.current.pad.gain, c.currentTime + 1.2);

    this.rhythmBus = c.createGain();
    this.rhythmBus.gain.value = 0;
    this.rhythmBus.connect(out);
    this.rhythmBus.gain.linearRampToValueAtTime(0.18, c.currentTime + 0.5);

    this.melodicBus = c.createGain();
    this.melodicBus.gain.value = 0;
    this.melodicBus.connect(out);
    this.melodicBus.gain.linearRampToValueAtTime(0.5, c.currentTime + 0.5);

    this.clusterBus = c.createGain();
    this.clusterBus.gain.value = 0;
    this.clusterBus.connect(out);

    // Reset clock — fresh start
    this.nextStepAt = c.currentTime + 0.05;
    this.currentStep = 0;
    this.currentBar = 0;
    this.currentChordIdx = -1;
    this.lastClusterChordIdx = -1;
    this.songStartCtxTime = c.currentTime;
  }

  // Queue a crossfade to a new song. Bar-aligned swap happens inside
  // tick(). If engine isn't running yet (first call, or post-stop()),
  // auto-start with the requested song so callers don't have to
  // sequence start/playSong themselves.
  //
  // Preview lock: while the user is previewing a song from settings,
  // ignore crossfade requests from main.ts's auto-selector so the
  // preview isn't immediately overridden by the per-frame pick.
  crossfadeTo(id: SongId, fadeMs = 1200) {
    if (this.previewLock && performance.now() < this.previewUntil) return;
    if (this.previewLock) this.previewLock = false; // expired — release
    if (!this.started) {
      this.current = SONGS[id];
      this.next = null;
      this.start();
      return;
    }
    if (this.current.id === id && !this.next) return;
    if (this.next && this.next.id === id) return;
    this.next = SONGS[id];
    this.nextFadeMs = fadeMs;
  }

  // Force a song to play and suppress auto-selection for `holdMs`.
  // Used by the settings modal so the player can audition each song.
  previewSong(id: SongId, holdMs = 12000) {
    this.previewLock = false; // bypass our own check
    this.crossfadeTo(id, 400);
    this.previewLock = true;
    this.previewUntil = performance.now() + holdMs;
  }

  cancelPreview() {
    this.previewLock = false;
    this.previewUntil = 0;
  }

  setTempoScale(scale: number) {
    const clamped = Math.max(0.25, Math.min(4.0, scale));
    this.tempoScale = clamped;
    try { localStorage.setItem('smp.tempoScale', String(clamped)); } catch { /* */ }
  }

  getTempoScale(): number { return this.tempoScale; }
  getCurrentSongId(): string { return this.current.id; }
  getNextSongId(): string | null { return this.next?.id ?? null; }
  isStarted(): boolean { return this.started; }
  getCurrentBar(): number { return this.currentBar; }
  getCurrentStep(): number { return this.currentStep; }
  getCurrentChordIdx(): number { return this.currentChordIdx; }
  getProgressionLength(): number { return this.current.progression.length; }
  getEffectiveBpm(): number {
    return this.current.bpm * (this.suddenDeath ? 1.4 : 1) * this.tempoScale;
  }
  getPreviewRemainingMs(): number {
    if (!this.previewLock) return 0;
    return Math.max(0, this.previewUntil - performance.now());
  }
  // Seconds elapsed in the current bar (0..barDur). Computed from
  // nextStepAt + currentStep relative to stepDur.
  getElapsedInBarSec(): number {
    const stepDur = 60 / this.getEffectiveBpm() / 4;
    return this.currentStep * stepDur;
  }
  getBarDurationSec(): number {
    const stepDur = 60 / this.getEffectiveBpm() / 4;
    return 16 * stepDur;
  }
  // Seconds since current song started (reset on swap). Timeline HUD
  // uses this so the user can say "cut at 12s" or "change BPM at 4s".
  getSongElapsedSec(): number {
    const c = ctx;
    if (!c || !this.started) return 0;
    return Math.max(0, c.currentTime - this.songStartCtxTime);
  }

  setIntensity(v: number) {
    this.intensity = Math.max(0, Math.min(1, v));
    const c = getCtx();
    if (!c || !this.padBus || !this.padFilter) return;
    const padTarget = this.current.pad.gain * (0.7 + this.intensity * 0.6);
    this.padBus.gain.linearRampToValueAtTime(padTarget, c.currentTime + 0.3);
    this.padFilter.frequency.linearRampToValueAtTime(
      this.current.pad.filterHz * (0.7 + this.intensity * 1.0),
      c.currentTime + 0.3,
    );
  }

  setSuddenDeath(on: boolean) {
    if (on === this.suddenDeath) return;
    this.suddenDeath = on;
    const c = getCtx();
    if (!c || !this.clusterBus) return;
    if (on) {
      this.clusterBus.gain.linearRampToValueAtTime(0.05, c.currentTime + 0.5);
    } else {
      this.clusterBus.gain.linearRampToValueAtTime(0, c.currentTime + 0.5);
      // Kill cluster oscs after the fade finishes.
      const grace = 600;
      setTimeout(() => {
        if (this.suddenDeath) return;
        for (const v of this.clusterVoices) {
          try { v.osc.stop(); } catch (_e) { /* already stopped */ }
        }
        this.clusterVoices = [];
        this.lastClusterChordIdx = -1;
      }, grace);
    }
  }

  // Per-frame: schedule any 16th-note steps that fall inside the
  // 2.0s lookahead horizon, advancing bar/step clocks. dt unused
  // (clock is wall-clock-driven via AudioContext) but kept for API
  // symmetry with prior tick(dt).
  //
  // Why 2.0s (was 0.25s): browsers throttle setInterval/rAF to ~1Hz
  // when the tab is backgrounded. A 0.25s lookahead + 1Hz tick means
  // ~0.75s of silence between every note batch — which reads as the
  // music freezing. 2.0s lookahead means one bg-throttled tick still
  // schedules two full seconds of audio ahead of the AudioContext
  // clock, so playback stays smooth while the game window is hidden.
  tick(_dt: number) {
    if (!this.started) return;
    const c = getCtx();
    if (!c || c.state === 'suspended') return;

    if (!this.firstAudibleTickLogged) {
      this.firstAudibleTickLogged = true;
      console.info('[music] first audible tick — ctxTime=', c.currentTime.toFixed(2), 'song=', this.current.id);
    }

    const bpm = this.current.bpm * (this.suddenDeath ? 1.4 : 1) * this.tempoScale;
    const stepDur = 60 / bpm / 4; // 16th-note duration

    const now = c.currentTime;
    const horizon = now + 2.0;
    if (this.nextStepAt < now) this.nextStepAt = now + 0.05;

    while (this.nextStepAt < horizon) {
      this.scheduleStep(this.nextStepAt, this.currentStep, stepDur);

      // Swing on 8ths — lengthen even-step, shorten odd-step in pairs.
      let nextDelta = stepDur;
      if (this.current.swing) {
        const sw = this.current.swing;
        if (this.currentStep % 2 === 0) nextDelta = stepDur * (1 + sw);
        else nextDelta = stepDur * (1 - sw);
      }
      this.nextStepAt += nextDelta;
      this.currentStep = (this.currentStep + 1) % 16;

      if (this.currentStep === 0) {
        this.currentBar++;
        // Bar boundary — pending crossfade fires here for musicality.
        if (this.next) this.swapSong();
      }
    }
  }

  // Hard-swap to next song at bar boundary. Pad voices crossfade
  // (old fades out, new fades in over ~0.15s) so no click.
  private swapSong() {
    if (!this.next) return;
    const c = getCtx();
    if (!c) return;
    this.current = this.next;
    this.next = null;
    // Rebuild pad on next scheduleStep(step=0).
    this.currentChordIdx = -1;
    this.lastClusterChordIdx = -1;
    this.songStartCtxTime = c.currentTime;
    this.currentBar = 0;
    // Update pad bus filter target to new song's baseline.
    if (this.padFilter) {
      this.padFilter.frequency.linearRampToValueAtTime(
        this.current.pad.filterHz * (0.7 + this.intensity * 1.0),
        c.currentTime + 0.3,
      );
    }
    if (this.padBus) {
      const padTarget = this.current.pad.gain * (0.7 + this.intensity * 0.6);
      this.padBus.gain.linearRampToValueAtTime(padTarget, c.currentTime + 0.3);
    }
  }

  private scheduleStep(at: number, step: number, stepDur: number) {
    const c = getCtx();
    if (!c) return;
    const song = this.current;
    const chordIdx = ((this.currentBar / song.barsPerChord) | 0) % song.progression.length;

    // Bar boundary — re-pitch pad voices (and cluster overlay if on).
    if (step === 0) {
      if (chordIdx !== this.currentChordIdx) {
        this.repitchPad(at, chordIdx);
        this.currentChordIdx = chordIdx;
      }
      if (this.suddenDeath && chordIdx !== this.lastClusterChordIdx) {
        this.repitchCluster(at, chordIdx);
        this.lastClusterChordIdx = chordIdx;
      }
    }

    const chordRoot = song.progression[chordIdx];

    // Bass
    const bassDeg = song.bass.steps[step];
    if (bassDeg !== null && bassDeg !== undefined) {
      const hz = degreeToHz(song, chordRoot, bassDeg, song.bass.octave);
      this.scheduleVoice(at, hz, stepDur * 0.9, song.bass.wave, song.bass.gain, this.melodicBus);
    }

    // Lead — gain scales with intensity (sparse → full).
    const leadDeg = song.lead.steps[step];
    if (leadDeg !== null && leadDeg !== undefined) {
      const leadVel = 0.4 + 0.6 * this.intensity;
      const hz = degreeToHz(song, chordRoot, leadDeg, song.lead.octave);
      this.scheduleVoice(at, hz, stepDur * 0.7, song.lead.wave, song.lead.gain * leadVel, this.melodicBus);
    }

    // Drums — kick always on, snare/hat gated by intensity floor.
    if (song.drums.kick[step]) this.scheduleKick(at);
    if (song.drums.snare[step] && this.intensity >= song.drums.snareVelMin) this.scheduleSnare(at);
    if (song.drums.hat[step] && this.intensity >= song.drums.hatVelMin) this.scheduleHat(at);

    // Sudden-death noise sweep on bar 1 of every 2-bar cycle.
    if (this.suddenDeath && step === 0 && (this.currentBar % 2) === 0) {
      this.scheduleSweep(at);
    }
  }

  private repitchPad(at: number, chordIdx: number) {
    const c = getCtx();
    if (!c || !this.padFilter) return;
    const song = this.current;
    const chordRoot = song.progression[chordIdx];
    const freqs = chordToHz(song, chordRoot, song.pad.voicing);
    const fadeOut = 0.18;

    // Fade existing voices, schedule kill.
    for (const v of this.padVoices) {
      try {
        v.gain.gain.cancelScheduledValues(at);
        v.gain.gain.setValueAtTime(v.gain.gain.value, at);
        v.gain.gain.linearRampToValueAtTime(0, at + fadeOut);
        v.osc.stop(at + fadeOut + 0.04);
      } catch (_e) { /* stale voice — ignore */ }
    }
    this.padVoices = [];

    // Spawn fresh voices, fade in over the same window for crossfade.
    for (const f of freqs) {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = song.pad.wave;
      osc.frequency.value = f;
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(1 / freqs.length, at + 0.18);
      osc.connect(g).connect(this.padFilter);
      osc.start(at);
      this.padVoices.push({ osc, gain: g });
    }
  }

  private repitchCluster(at: number, chordIdx: number) {
    const c = getCtx();
    if (!c || !this.clusterBus) return;
    const song = this.current;
    const chordRoot = song.progression[chordIdx];
    const freqs = chordToHz(song, chordRoot, 'cluster');
    const fadeOut = 0.2;

    for (const v of this.clusterVoices) {
      try {
        v.gain.gain.cancelScheduledValues(at);
        v.gain.gain.setValueAtTime(v.gain.gain.value, at);
        v.gain.gain.linearRampToValueAtTime(0, at + fadeOut);
        v.osc.stop(at + fadeOut + 0.04);
      } catch (_e) { /* stale */ }
    }
    this.clusterVoices = [];

    for (const f of freqs) {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = 'sawtooth';
      osc.frequency.value = f;
      g.gain.setValueAtTime(0, at);
      g.gain.linearRampToValueAtTime(1 / freqs.length, at + 0.2);
      osc.connect(g).connect(this.clusterBus);
      osc.start(at);
      this.clusterVoices.push({ osc, gain: g });
    }
  }

  private scheduleVoice(at: number, hz: number, dur: number, wave: OscillatorType, vol: number, bus: GainNode | null) {
    const c = getCtx();
    if (!c || !bus) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = wave;
    osc.frequency.value = hz;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(vol, at + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    osc.connect(g).connect(bus);
    osc.start(at);
    osc.stop(at + dur + 0.02);
  }

  private scheduleKick(at: number) {
    const c = getCtx();
    if (!c || !this.rhythmBus) return;
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(120, at);
    osc.frequency.exponentialRampToValueAtTime(40, at + 0.12);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.7, at + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.18);
    osc.connect(g).connect(this.rhythmBus);
    osc.start(at);
    osc.stop(at + 0.2);
  }

  private scheduleSnare(at: number) {
    const c = getCtx();
    if (!c || !this.rhythmBus) return;
    const buf = c.createBuffer(1, 0.18 * c.sampleRate, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.6;
    const src = c.createBufferSource();
    const filt = c.createBiquadFilter();
    const g = c.createGain();
    src.buffer = buf;
    filt.type = 'bandpass';
    filt.frequency.value = 1400;
    filt.Q.value = 0.9;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.45, at + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.14);
    src.connect(filt).connect(g).connect(this.rhythmBus);
    src.start(at);
    src.stop(at + 0.18);
  }

  private scheduleHat(at: number) {
    const c = getCtx();
    if (!c || !this.rhythmBus) return;
    const buf = c.createBuffer(1, 0.04 * c.sampleRate, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * 0.5;
    const src = c.createBufferSource();
    const filt = c.createBiquadFilter();
    const g = c.createGain();
    src.buffer = buf;
    filt.type = 'highpass';
    filt.frequency.value = 6000;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.16, at + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.04);
    src.connect(filt).connect(g).connect(this.rhythmBus);
    src.start(at);
    src.stop(at + 0.05);
  }

  private scheduleSweep(at: number) {
    const c = getCtx();
    if (!c || !this.clusterBus) return;
    const dur = 0.9;
    const buf = c.createBuffer(1, dur * c.sampleRate, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = c.createBufferSource();
    const filt = c.createBiquadFilter();
    const g = c.createGain();
    src.buffer = buf;
    filt.type = 'highpass';
    filt.frequency.setValueAtTime(200, at);
    filt.frequency.exponentialRampToValueAtTime(7000, at + dur * 0.9);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.5, at + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    src.connect(filt).connect(g).connect(this.clusterBus);
    src.start(at);
    src.stop(at + dur + 0.02);
  }

  stop(fadeMs = 600) {
    if (!this.started) return;
    const c = getCtx();
    if (!c) return;
    const fadeS = fadeMs / 1000;
    if (this.padBus) this.padBus.gain.linearRampToValueAtTime(0, c.currentTime + fadeS);
    if (this.rhythmBus) this.rhythmBus.gain.linearRampToValueAtTime(0, c.currentTime + fadeS);
    if (this.melodicBus) this.melodicBus.gain.linearRampToValueAtTime(0, c.currentTime + fadeS);
    if (this.clusterBus) this.clusterBus.gain.linearRampToValueAtTime(0, c.currentTime + fadeS);
    setTimeout(() => {
      for (const v of this.padVoices) { try { v.osc.stop(); } catch (_e) { /* */ } }
      for (const v of this.clusterVoices) { try { v.osc.stop(); } catch (_e) { /* */ } }
      this.padVoices = [];
      this.clusterVoices = [];
      this.currentChordIdx = -1;
      this.lastClusterChordIdx = -1;
      this.suddenDeath = false;
      this.started = false;
    }, fadeMs + 80);
  }
}

const music = new MusicEngine();

let lastFootstep = 0;

export const SFX = {
  tag() { blip(720, 0.18, 'square', 0.1); },
  hood() { sweep(420, 140, 0.32, 'triangle', 0.12); },
  hoodOff() { sweep(140, 380, 0.18, 'triangle', 0.08); },
  // Sprint engagement whoosh — short rising noise + tone hybrid so a
  // stamped Shift reads as "POWER ON" without smothering combat audio.
  dash() {
    noiseBurst(0.06, 0.05, 4200);
    blip(720, 0.07, 'triangle', 0.07);
    setTimeout(() => blip(1080, 0.05, 'sine', 0.05), 40);
  },
  tether() { blip(420, 0.12, 'sawtooth', 0.06); },
  tetherStart() { tetherDrone.start(); blip(340, 0.18, 'sawtooth', 0.07); },
  tetherEnd() { tetherDrone.stop(); },
  eliminate() {
    sweep(560, 90, 0.45, 'sawtooth', 0.1);
    scheduledNoiseBurst(0.06, 0.18, 0.05, 1200);
  },
  exhausted() {
    sweep(380, 110, 0.55, 'square', 0.08);
  },
  zoneEnter() {
    blip(880, 0.14, 'sine', 0.06);
    scheduledBlip(0.07, 1320, 0.18, 'sine', 0.05);
  },
  win() {
    blip(700, 0.2, 'sine', 0.1);
    setTimeout(() => blip(900, 0.2, 'sine', 0.1), 150);
    setTimeout(() => blip(1200, 0.3, 'sine', 0.1), 300);
  },
  lose() {
    blip(280, 0.25, 'sawtooth', 0.08);
    setTimeout(() => blip(200, 0.3, 'sawtooth', 0.08), 180);
  },
  startRound() {
    blip(523, 0.12, 'sine', 0.08);
    setTimeout(() => blip(659, 0.12, 'sine', 0.08), 110);
    setTimeout(() => blip(784, 0.18, 'sine', 0.08), 220);
  },
  // Rising 3-note chord when the player's ultimate fills. Higher
  // than startRound + sawtooth for a "charge complete" read.
  ultReady() {
    blip(659,  0.10, 'triangle', 0.09);
    setTimeout(() => blip(880,  0.10, 'triangle', 0.09),  80);
    setTimeout(() => blip(1175, 0.22, 'sawtooth', 0.11), 170);
  },
  // Punchy high-pitch blip that overlays the normal hit sound when
  // a damage tick rolls a crit. Sharp, short, unmistakable.
  critHit() {
    blip(1400, 0.05, 'square',   0.11);
    setTimeout(() => blip(1800, 0.08, 'triangle', 0.09), 20);
  },
  // B5 — Gold counter-hit sting. Fires when the attacker lands a hit
  // on a foe who dodged them <300ms ago (punish window). High-pitched
  // rising blip + short noise whiff so it reads distinct from crit.
  counterHit() {
    blip(1600, 0.06, 'square',   0.11);
    setTimeout(() => blip(2000, 0.09, 'triangle', 0.10), 30);
    setTimeout(() => noiseBurst(0.10, 0.06, 6000),        40);
  },
  // Rising horn stab that plays across the match when anyone reaches
  // bounty tier — fires once per player-per-bounty so the server
  // broadcasts it.
  bountyOn() {
    blip(392, 0.14, 'sawtooth', 0.10);
    setTimeout(() => blip(587, 0.16, 'sawtooth', 0.11), 110);
    setTimeout(() => blip(784, 0.22, 'sawtooth', 0.12), 240);
  },
  // Triumph cymbal-crash-adjacent sound for killing a bounty player.
  bountyKill() {
    noiseBurst(0.28, 0.10, 5000);
    blip(1200, 0.18, 'triangle', 0.10);
    setTimeout(() => blip(1600, 0.14, 'triangle', 0.08), 80);
  },
  // D1/D2 — "voice line" stings for emotes + own death. Uses pitched
  // sawtooth stacks with short decays to suggest vocalization without
  // shipping sampled audio (instant-load intact). Virgin lines run
  // higher + giggly; Chad lines run lower + confident.
  voice: {
    virginDance() {
      blip(880,  0.08, 'triangle', 0.08);
      setTimeout(() => blip(1040, 0.10, 'triangle', 0.08),  90);
      setTimeout(() => blip(1320, 0.14, 'sine',     0.07), 190);
    },
    virginFlex() {
      blip(780, 0.22, 'sawtooth', 0.09);
      setTimeout(() => blip(980, 0.20, 'sawtooth', 0.08), 160);
    },
    virginBow() {
      blip(360, 0.14, 'triangle', 0.06);
      setTimeout(() => blip(280, 0.20, 'sine',     0.06), 110);
    },
    virginLaugh() {
      blip(700, 0.06, 'square', 0.08);
      setTimeout(() => blip(760, 0.06, 'square', 0.08),  90);
      setTimeout(() => blip(820, 0.10, 'square', 0.08), 180);
    },
    chadDance() {
      blip(260, 0.14, 'sawtooth', 0.10);
      setTimeout(() => blip(340, 0.16, 'sawtooth', 0.10), 120);
    },
    chadFlex() {
      blip(200, 0.10, 'sawtooth', 0.10);
      setTimeout(() => blip(300, 0.12, 'sawtooth', 0.10), 110);
      setTimeout(() => blip(440, 0.22, 'sawtooth', 0.12), 230);
    },
    chadBow() {
      blip(150, 0.28, 'sawtooth', 0.10);
    },
    chadLaugh() {
      blip(280, 0.10, 'sawtooth', 0.10);
      setTimeout(() => blip(340, 0.10, 'sawtooth', 0.10), 130);
      setTimeout(() => blip(300, 0.12, 'sawtooth', 0.09), 260);
    },
    virginDeath() {
      // "noooo" falling cry.
      blip(700, 0.12, 'sawtooth', 0.09);
      setTimeout(() => blip(540, 0.14, 'sawtooth', 0.09), 140);
      setTimeout(() => blip(360, 0.22, 'sawtooth', 0.08), 290);
    },
    chadDeath() {
      // Grunted "ugh" — short, flat low.
      blip(220, 0.14, 'sawtooth', 0.11);
      setTimeout(() => blip(180, 0.18, 'sawtooth', 0.10), 120);
    },
    // Kill stings — sharp + short so they ride alongside crit/counter
    // hit SFX without smearing. Virgin = giddy "yatta" rise; Chad = a
    // single throaty bark.
    virginKill() {
      blip(880, 0.06, 'square',   0.09);
      setTimeout(() => blip(1175, 0.10, 'triangle', 0.09),  60);
      setTimeout(() => blip(1480, 0.12, 'sine',     0.07), 150);
    },
    chadKill() {
      blip(180, 0.08, 'sawtooth', 0.12);
      setTimeout(() => blip(260, 0.14, 'sawtooth', 0.10),  60);
    },
    // Ult cast battle cry — fires when player triggers their ult
    // ("Q" for Virgin slam, ult-charge spend for Chad). Long, declarative.
    virginUlt() {
      blip(620, 0.10, 'sawtooth', 0.10);
      setTimeout(() => blip(820,  0.12, 'sawtooth', 0.10),  90);
      setTimeout(() => blip(1100, 0.20, 'triangle', 0.10), 200);
    },
    chadUlt() {
      blip(160, 0.14, 'sawtooth', 0.12);
      setTimeout(() => blip(240, 0.18, 'sawtooth', 0.12), 120);
      setTimeout(() => blip(330, 0.26, 'sawtooth', 0.11), 260);
    },
    // Mythic cast — shipped a tier above ult so the rare R-key feels
    // like Going Super Saiyan. Virgin = ascending arpeggio,
    // Chad = power-up grunt.
    virginMythic() {
      blip(660,  0.08, 'square',   0.10);
      setTimeout(() => blip(880,  0.10, 'triangle', 0.10),  70);
      setTimeout(() => blip(1175, 0.12, 'triangle', 0.10), 160);
      setTimeout(() => blip(1480, 0.18, 'sine',     0.09), 270);
    },
    chadMythic() {
      blip(140, 0.10, 'sawtooth', 0.13);
      setTimeout(() => blip(220, 0.14, 'sawtooth', 0.12), 110);
      setTimeout(() => blip(300, 0.20, 'sawtooth', 0.11), 250);
      setTimeout(() => blip(380, 0.22, 'sawtooth', 0.10), 380);
    },
    // Level-up cheer — short, celebratory. Tier above kill so a
    // streaky run reads even louder when you cap a kill into a level.
    virginLevelUp() {
      blip(880,  0.06, 'square',   0.10);
      setTimeout(() => blip(1175, 0.08, 'triangle', 0.10),  60);
      setTimeout(() => blip(1480, 0.10, 'triangle', 0.10), 130);
      setTimeout(() => blip(1760, 0.14, 'sine',     0.09), 220);
    },
    chadLevelUp() {
      blip(220, 0.08, 'sawtooth', 0.13);
      setTimeout(() => blip(330, 0.10, 'sawtooth', 0.12), 100);
      setTimeout(() => blip(440, 0.16, 'sawtooth', 0.10), 200);
    },
  },
  footstep() {
    const now = performance.now();
    if (now - lastFootstep < 220) return;
    lastFootstep = now;
    noiseBurst(0.05, 0.025, 800);
  },
  door() {
    blip(180, 0.08, 'sine', 0.05);
    setTimeout(() => blip(120, 0.12, 'sine', 0.04), 50);
  },
  money() {
    blip(1200, 0.06, 'sine', 0.06);
    setTimeout(() => blip(1600, 0.06, 'sine', 0.06), 60);
    setTimeout(() => blip(2000, 0.1, 'sine', 0.05), 120);
  },
  itemUse() {
    blip(560, 0.08, 'triangle', 0.05);
    setTimeout(() => blip(740, 0.1, 'triangle', 0.04), 40);
  },
  alert() {
    sweep(880, 540, 0.3, 'sawtooth', 0.08);
    setTimeout(() => sweep(880, 540, 0.3, 'sawtooth', 0.08), 350);
  },
  heartbeat() {
    blip(60, 0.1, 'sine', 0.08);
    setTimeout(() => blip(60, 0.08, 'sine', 0.06), 200);
  },
  // Camera shutter click for Chad selfie attacks — short + snappy.
  phoneClick() {
    noiseBurst(0.04, 0.035, 4800);
    blip(2100, 0.03, 'square', 0.05);
  },
  // Page-flip whoosh for Virgin manga attacks — soft filtered noise.
  pageFlip() {
    noiseBurst(0.14, 0.03, 900);
    scheduledBlip(0.04, 240, 0.06, 'sine', 0.03);
  },
  craft() {
    blip(440, 0.08, 'square', 0.06);
    setTimeout(() => blip(660, 0.08, 'square', 0.06), 80);
    setTimeout(() => blip(880, 0.12, 'square', 0.06), 160);
  },
  jail() {
    sweep(220, 110, 0.4, 'square', 0.09);
    setTimeout(() => noiseBurst(0.3, 0.04, 800), 100);
  },
  levelUp() {
    blip(523, 0.1, 'square', 0.07);
    setTimeout(() => blip(659, 0.1, 'square', 0.07), 90);
    setTimeout(() => blip(784, 0.1, 'square', 0.07), 180);
    setTimeout(() => blip(1047, 0.18, 'square', 0.07), 270);
  },
  carEnter() {
    blip(220, 0.1, 'square', 0.06);
    setTimeout(() => blip(180, 0.12, 'square', 0.05), 80);
  },
  carEngine() {
    sweep(80, 200, 0.5, 'sawtooth', 0.06);
  },
  // Finale stings — played during the end-of-match cinematic. Each is
  // short and distinct so viewers learn to recognize "oh, MVP reveal"
  // vs "VIRGIN WIN" vs "CHAD WIN" on sound alone.
  mvpSting() {
    // Ascending C-E-G arpeggio — builds tension before the spotlight lands.
    blip(523, 0.18, 'sine', 0.1);       // C5
    setTimeout(() => blip(659, 0.18, 'sine', 0.1), 120);  // E5
    setTimeout(() => blip(784, 0.3, 'sine', 0.11), 240);  // G5
    setTimeout(() => scheduledNoiseBurst(0.0, 0.12, 0.04, 2400), 360);
  },
  victorySting: {
    // Virgin — cold, bell-like, sparse (shakuhachi-ish feel).
    virgin() {
      blip(1046, 0.22, 'sine', 0.09);              // C6
      setTimeout(() => blip(1760, 0.22, 'sine', 0.08), 160);  // A6
      setTimeout(() => blip(1319, 0.45, 'sine', 0.1), 320);   // E6
      setTimeout(() => noiseBurst(0.3, 0.02, 4400), 360);
    },
    // Chad — warm power chord, triumphant.
    chad() {
      blip(165, 0.55, 'sawtooth', 0.09);           // E3
      blip(208, 0.55, 'sawtooth', 0.08);           // G#3
      blip(247, 0.55, 'sawtooth', 0.08);           // B3
      setTimeout(() => blip(330, 0.4, 'triangle', 0.08), 200); // E4
      setTimeout(() => noiseBurst(0.25, 0.05, 1200), 100);
    },
    // Draw — single low gong for a "no-winner" beat.
    draw() {
      blip(65, 0.9, 'sine', 0.12);                 // C2
      setTimeout(() => blip(98, 0.6, 'triangle', 0.08), 120); // G2
      setTimeout(() => noiseBurst(0.5, 0.03, 300), 0);
    },
  },
  music: {
    start: () => music.start(),
    playSong: (id: SongId, fadeMs = 1200) => music.crossfadeTo(id, fadeMs),
    previewSong: (id: SongId, holdMs = 12000) => music.previewSong(id, holdMs),
    cancelPreview: () => music.cancelPreview(),
    setTempoScale: (scale: number) => music.setTempoScale(scale),
    getTempoScale: () => music.getTempoScale(),
    // Timeline HUD readouts — main.ts polls these per frame.
    getCurrentSongId: () => music.getCurrentSongId(),
    getSongElapsedSec: () => music.getSongElapsedSec(),
    getCurrentBar: () => music.getCurrentBar(),
    getCurrentChordIdx: () => music.getCurrentChordIdx(),
    getProgressionLength: () => music.getProgressionLength(),
    getEffectiveBpm: () => music.getEffectiveBpm(),
    getBarDurationSec: () => music.getBarDurationSec(),
    setIntensity: (v: number) => music.setIntensity(v),
    setSuddenDeath: (on: boolean) => music.setSuddenDeath(on),
    tick: (dt: number) => music.tick(dt),
    stop: (fadeMs = 600) => music.stop(fadeMs),
  },
};

// Re-export song registry so the settings modal can list them.
export { SONGS } from './songs';

// Re-export so callers can `import { SongId } from './audio/synth'`.
export type { SongId } from './songs';

// Resume audio context on first user interaction (browser autoplay policy).
if (typeof window !== 'undefined') {
  const resume = () => {
    const c = getCtx();
    if (c && c.state === 'suspended') {
      c.resume().catch((e) => console.warn('[audio] resume failed', e));
    }
  };
  window.addEventListener('pointerdown', resume, { once: false });
  window.addEventListener('keydown', resume, { once: false });
  window.addEventListener('touchstart', resume, { once: false });
  window.addEventListener('click', resume, { once: false });
  // Dev-only diagnostic API. Gated on Vite's build-time
  // `import.meta.env.DEV` so the whole `window.smpAudio` assignment
  // + string literals like `'smp.debugEnabled'` get dead-code
  // eliminated in prod builds (scripts/verify-bundle.mjs enforces
  // this). In dev the hook is always on.
  if (import.meta.env.DEV) {
    (window as unknown as { smpAudio: unknown }).smpAudio = {
      state: () => audioDiag(),
      resume: () => resumeAudio(),
      testBeep: () => testBeep(),
      setMusicVolume,
      setSfxVolume,
      playSong: (id: SongId) => music.crossfadeTo(id, 400),
      previewSong: (id: SongId, holdMs = 12000) => music.previewSong(id, holdMs),
      cancelPreview: () => music.cancelPreview(),
      setTempoScale: (s: number) => music.setTempoScale(s),
    };
  }
}
